import type { ReactNode } from "react";
import type { ChatStatus } from "ai";
import ErrorAlert from "../../../design-system/primitives/components/ErrorAlert/ErrorAlert";
import Text from "../../../design-system/primitives/components/Text/Text";
import { icons } from "../../../design-system/primitives/icons";
import { AssistantThinking, paragraphsOf } from "./components/ChatMessage";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from "./components/MessageScroller";
import { PromptInput } from "./components/PromptInput";
import { Sources } from "./components/Sources";
import { Suggestions } from "./components/Suggestions";
import type { SparkChatMessage } from "./types";
import { sourcesOf, textOf } from "./types";
import styles from "./chatBlock.css";

const SparklesIcon = icons.sparkles;

export type SparkChatBlockViewProps = {
  greeting: string;
  suggestions?: string[];
  placeholder?: string;
  maxMessageLength: number;
  messages: SparkChatMessage[];
  status: ChatStatus;
  errorMessage?: string;
  disabled?: boolean;
  note?: string;
  onSend: (text: string) => void;
};

const Paragraphs = ({ text, color }: { text: string; color?: "inherit" }) =>
  paragraphsOf(text).map((paragraph, i) => (
    <Text key={i} variant="body" color={color}>
      {paragraph}
    </Text>
  ));

const AssistantTurn = ({
  text,
  children,
}: {
  text: string;
  children?: ReactNode;
}) => (
  <div className={styles.assistantMessage}>
    <div className={styles.assistantText}>
      <Paragraphs text={text} />
    </div>
    {children}
  </div>
);

export const SparkChatBlockView = ({
  greeting,
  suggestions = [],
  placeholder = "Schreib deine Antwort …",
  maxMessageLength,
  messages,
  status,
  errorMessage,
  disabled = false,
  note,
  onSend,
}: SparkChatBlockViewProps) => {
  const busy = status === "submitted" || status === "streaming";
  const lastMessage = messages.at(-1);
  const waiting =
    status === "submitted" ||
    (status === "streaming" &&
      (lastMessage?.role !== "assistant" || textOf(lastMessage) === ""));

  return (
    <section className={styles.card} aria-label="KI-Assistent">
      <header className={styles.header}>
        <SparklesIcon className={styles.headerIcon} size={20} aria-hidden />
        <Text variant="label" as="h3">
          KI-Assistent
        </Text>
      </header>

      <div className={styles.transcript}>
        <MessageScrollerProvider autoScroll scrollPreviousItemPeek={48}>
          <MessageScroller>
            <MessageScrollerViewport aria-label="Nachrichten">
              <MessageScrollerContent className={styles.transcriptContent}>
                <MessageScrollerItem>
                  <AssistantTurn text={greeting}>
                    {messages.length === 0 && (
                      <Suggestions
                        variant="bubble"
                        suggestions={suggestions}
                        onSelect={onSend}
                        disabled={busy || disabled}
                      />
                    )}
                  </AssistantTurn>
                </MessageScrollerItem>

                {messages.map((message) => {
                  const text = textOf(message);
                  if (message.role === "user") {
                    return (
                      <MessageScrollerItem
                        key={message.id}
                        messageId={message.id}
                        scrollAnchor
                      >
                        <div className={styles.userMessage}>
                          <Paragraphs text={text} color="inherit" />
                        </div>
                      </MessageScrollerItem>
                    );
                  }
                  if (!text) return null;
                  return (
                    <MessageScrollerItem
                      key={message.id}
                      messageId={message.id}
                    >
                      <AssistantTurn text={text}>
                        <Sources sources={sourcesOf(message)} />
                      </AssistantTurn>
                    </MessageScrollerItem>
                  );
                })}

                {waiting && (
                  <MessageScrollerItem>
                    <AssistantThinking />
                  </MessageScrollerItem>
                )}

                {errorMessage && (
                  <MessageScrollerItem>
                    <ErrorAlert title={errorMessage} />
                  </MessageScrollerItem>
                )}
              </MessageScrollerContent>
            </MessageScrollerViewport>
            <MessageScrollerButton />
          </MessageScroller>
        </MessageScrollerProvider>
      </div>

      <footer className={styles.footer}>
        {note ? (
          <Text variant="body" color="neutral-100">
            {note}
          </Text>
        ) : (
          <PromptInput
            variant="inline"
            placeholder={placeholder}
            onSubmit={onSend}
            busy={busy || disabled}
            maxLength={maxMessageLength}
          />
        )}
        <Text variant="label-small" color="neutral-100">
          KI kann Fehler machen. Gib keine persönlichen Daten ein.
        </Text>
      </footer>
    </section>
  );
};
